import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { AdminModal, AdminField, AdminTable, AdminPageHeader, ImageUploadField, toSlug } from "./AdminBits";
import { StatusBadge } from "@/components/common/Bits";
import RichTextEditor from "@/components/common/RichTextEditor";

const RichField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">{label}</label>
    <RichTextEditor value={value} onChange={onChange} testid={testid}/>
  </div>
);

const emptyProject = {
  title_tr: "", title_en: "", title_ar: "", slug: "",
  short_description_tr: "", short_description_en: "", short_description_ar: "",
  content_tr: "", content_en: "", content_ar: "",
  category: "education", status: "planned", country: "", city: "",
  start_date: "", end_date: "", cover_image: "",
  gallery_images: [], impact_summary_tr: "", impact_summary_en: "", impact_summary_ar: "",
  partners: [], supporters: [], cta_label_tr: "Bilgi Al", cta_url: "/iletisim",
  seo_title: "", seo_description: "", status_publication: "published",
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyProject);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/projects?published_only=false&limit=200");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setF(emptyProject); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setF({ ...emptyProject, ...r }); setOpen(true); };
  const del = async (r) => { await api.delete(`/projects/${r.id}`); toast.success("Silindi"); load(); };

  const onChange = (e) => {
    const v = e.target.value;
    setF(prev => {
      const next = { ...prev, [e.target.name]: v };
      if (e.target.name === "title_tr" && !editing) next.slug = toSlug(v);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, f);
        toast.success("Güncellendi");
      } else {
        await api.post("/projects", f);
        toast.success("Oluşturuldu");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Hata");
    } finally { setSaving(false); }
  };

  const cols = [
    { key: "title_tr", label: "Başlık", render: r => <div><div className="font-medium">{r.title_tr}</div><div className="text-xs text-[#4B5563]">/{r.slug}</div></div> },
    { key: "category", label: "Kategori" },
    { key: "status", label: "Durum", render: r => <StatusBadge status={r.status}/> },
    { key: "country", label: "Ülke" },
    { key: "status_publication", label: "Yayın", render: r => <span className={`text-xs px-2 py-0.5 rounded-full ${r.status_publication === "published" ? "bg-[#EAF7F0] text-[#0F5132]" : "bg-gray-100 text-gray-600"}`}>{r.status_publication}</span> },
  ];

  return (
    <div data-testid="admin-projects">
      <AdminPageHeader title="Projeler" subtitle={`${items.length} proje`} action={<button onClick={startCreate} className="btn-primary !py-2 !px-4 text-sm" data-testid="btn-new-project"><Plus className="w-4 h-4"/> Yeni Proje</button>}/>
      <AdminTable columns={cols} rows={items} onEdit={startEdit} onDelete={del} testid="table-projects"/>

      <AdminModal open={open} onClose={() => setOpen(false)} title={editing ? "Projeyi Düzenle" : "Yeni Proje"} testid="project-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <AdminField label="Başlık (TR)" name="title_tr" value={f.title_tr} onChange={onChange} required testid="pf-title-tr"/>
            <AdminField label="Slug" name="slug" value={f.slug} onChange={onChange} required testid="pf-slug"/>
            <AdminField label="Başlık (EN)" name="title_en" value={f.title_en} onChange={onChange} testid="pf-title-en"/>
            <AdminField label="Başlık (AR)" name="title_ar" value={f.title_ar} onChange={onChange} testid="pf-title-ar"/>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">Kategori</label>
              <select name="category" value={f.category} onChange={onChange} className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm" data-testid="pf-category">
                <option value="education">Eğitim</option>
                <option value="health">Sağlık</option>
                <option value="housing">Barınma</option>
                <option value="infrastructure">Altyapı</option>
                <option value="disaster">Afet</option>
                <option value="economic">Ekonomik</option>
                <option value="culture">Kültür</option>
                <option value="sustainable">Sürdürülebilir</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">Durum</label>
              <select name="status" value={f.status} onChange={onChange} className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm" data-testid="pf-status">
                <option value="planned">Planlanan</option>
                <option value="ongoing">Devam Eden</option>
                <option value="completed">Tamamlanan</option>
                <option value="preparing">Hazırlık</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">Yayın</label>
              <select name="status_publication" value={f.status_publication} onChange={onChange} className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm" data-testid="pf-pub">
                <option value="published">Yayında</option>
                <option value="draft">Taslak</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <AdminField label="Ülke" name="country" value={f.country} onChange={onChange} testid="pf-country"/>
            <AdminField label="Şehir" name="city" value={f.city} onChange={onChange} testid="pf-city"/>
            <AdminField label="Başlangıç Tarihi" name="start_date" value={f.start_date} onChange={onChange} testid="pf-start"/>
            <AdminField label="Bitiş Tarihi" name="end_date" value={f.end_date} onChange={onChange} testid="pf-end"/>
          </div>
          <ImageUploadField label="Kapak Görseli" value={f.cover_image} onChange={(v) => setF(p => ({ ...p, cover_image: v }))} testid="pf-cover"/>
          <AdminField label="Kısa Açıklama (TR)" name="short_description_tr" value={f.short_description_tr} onChange={onChange} isTextArea rows={2} testid="pf-short-tr"/>
          <AdminField label="Kısa Açıklama (EN)" name="short_description_en" value={f.short_description_en} onChange={onChange} isTextArea rows={2} testid="pf-short-en"/>
          <RichField label="İçerik (TR) — Zengin Metin" value={f.content_tr} onChange={(v) => setF(p => ({ ...p, content_tr: v }))} testid="pf-content-tr"/>
          <RichField label="İçerik (EN)" value={f.content_en} onChange={(v) => setF(p => ({ ...p, content_en: v }))} testid="pf-content-en"/>
          <RichField label="İçerik (AR)" value={f.content_ar} onChange={(v) => setF(p => ({ ...p, content_ar: v }))} testid="pf-content-ar"/>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline !py-2 !px-4 text-sm">İptal</button>
            <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm" data-testid="pf-submit"><Save className="w-4 h-4"/> {saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
